/**
 * 需要想定 daily 編集画面のセクションヘッダー行。
 * デザイン .dft-section-header-row に準拠。
 *
 * メタ情報バー: ワークフローID + 対象日 + 計画断面 + ステータス (未着手/進行中/完了/削除
 * バッジ) + 確定日時/確定者 (需要想定の確定記録がある場合のみ) を label:value 形式で
 * 表示する。ワークフロー全体の完了日時・完了者は本画面の対象外 (AC-DFD-053、issue
 * #8453)。
 * design 参照: design/demand/forecast-today.html .dft-section-header-row
 *   (L502-511 markup + L1697-1713 ステータス反映 JS + L2303-2309 確定情報反映 JS)
 */
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { DayType } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { SingularDayCategory } from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import { WorkflowCaseStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  type BadgeProps,
  Flex,
  Link,
  Text,
} from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { WorkflowCaseStatusBadge } from '~/components/common/WorkflowCaseStatusBadge'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { formatDateTimeLocaleJa } from '~/models/dateTime'
import { SingularDayMarker } from '~/pages/DemandForecastDaily/components/SingularDayMarker'
import type { SectionSegment } from '~/pages/DemandForecastDaily/models/types'
import { routePaths } from '~/routes/routePaths'

type Props = {
  caseId?: string
  /**
   * human-readable なワークフロー業務キー (例: `D0-20260729-27D5`)。表示テキストは
   * これを使い、リンク先 (WorkflowCaseDetail) は caseId (UUID) を使う
   * (ADR workflow-human-readable-id-separate-from-pk)。fetch 前で未解決のときは
   * caseId (UUID) にフォールバックする。
   */
  workflowId?: string
  sectionSegment?: SectionSegment
  showTargetDate: boolean
  targetDateLabel: string
  /**
   * 対象日の曜日 (日本語 1 文字、design L1771-1778 準拠)。この画面は caseId 必須で
   * 常に対象日ラベルの後ろに「(曜)」を付与する。undefined は非表示 (対象日未解決)。
   */
  dayOfWeekLabel?: string
  /**
   * 対象日ラベルに埋め込む業務エリア名 (design L1764-1778 準拠)。caseId 経由は
   * 業務エリアが Toolbar から非表示化される代わりにここへ表示する。
   */
  areaLabel?: string
  /**
   * 需要想定サブステップ軸のステータス (backend-computed derived field, issue
   * #6624: 需要想定の一覧/詳細ステータス食い違い修正)。需要想定一覧
   * (DemandForecastListPage) と同軸、backend の
   * LoadedCase.DeriveDemandForecastListStatus 由来。ケース全体軸の
   * WorkflowCase.status (DeriveCaseStatus) とは別軸。prop 名で軸を明示し、
   * 呼び出し側が誤って `wfCase.status` (ケース全体軸) を渡すのを防ぐ
   * (index.tsx `wfCase?.demandForecastStatus`)。GetWorkflowCase でのみ
   * populate される (demand_step_confirmed と同じゲート)。未取得/UNSPECIFIED
   * はバッジを描画しない。
   */
  demandForecastStatus?: WorkflowCaseStatus
  /**
   * 需要想定の確定日時 (AC-DFD-050/051, issue #8453)。本画面の確定操作
   * (AC-DFC-005) で記録された確定記録の confirmed_at。需要想定が確定済みの
   * ときのみ表示する (design L772-773 dft-section-confirmed-at-wrap)。
   * ワークフロー全体の完了日時 (WorkflowCase.completedAt) とは別軸で、本画面は
   * 表示しない (AC-DFD-053)。
   */
  confirmedAt?: Timestamp
  /**
   * 需要想定の確定者の eauth member ID (= auth0 subject)。confirmedAt とセットで
   * 確定済みのときのみ表示する (design L772-773 dft-section-confirmed-by-wrap)。
   * 表示時に useMemberDisplayName で表示名へ解決し、生 ID は出さない (#6538)。
   */
  confirmedBy?: string
  /**
   * 対象日の 4 区分判別 (AC-26, spec AC-036: 平日/土曜/日祝/特異日)。backend
   * `target_date_day_type` をそのまま渡す表示専用 prop。UNSPECIFIED / 未指定は
   * 非表示 (対象日未解決)。
   */
  dayType?: DayType
  /**
   * 特異日マスタ登録名 (spec AC-036a)。dayType === SINGULAR のときのみ日付
   * マーカー (SingularDayMarker) を描画する条件に使う。バッジ本体には出さない
   * (AC-07)。
   */
  singularDayName?: string
  /** 特異日のカテゴリ。singularDayName とセットでマーカーのホバー内容に使う。 */
  singularDayCategory?: SingularDayCategory
}

/**
 * dayType → Badge status のマッピング (design demand/forecast-today.html
 * .section-title-badge.day-badge-kind の色分けに突合、AC-05):
 *   WEEKDAY (無印)  → neutral  (surface-secondary / text-mid-emphasis)
 *   SATURDAY (.kind-sat) → info (surface-info-low / text-link)
 *   HOLIDAY (.kind-holiday) → negative (surface-negative-low / text-negative-mid)
 *   SINGULAR (.kind-special) → warning (surface-warning-low / text-warning-high)
 * UNSPECIFIED / 未定義値は neutral にフォールバックする。
 */
export function dayTypeBadgeStatus(dayType: DayType): BadgeProps['status'] {
  switch (dayType) {
    case DayType.SATURDAY:
      return 'info'
    case DayType.HOLIDAY:
      return 'negative'
    case DayType.SINGULAR:
      return 'warning'
    case DayType.WEEKDAY:
    default:
      return 'neutral'
  }
}

const sectionSegmentToI18nKey: Record<SectionSegment, string> = {
  today: 'page:demandForecastDaily.header.sectionToday',
  tomorrow: 'page:demandForecastDaily.header.sectionTomorrow',
  dayaftertomorrow: 'page:demandForecastDaily.header.sectionDayAfterTomorrow',
}

const dayTypeToI18nKey: Partial<Record<DayType, string>> = {
  [DayType.WEEKDAY]: 'page:demandForecastDaily.header.dayType.weekday',
  [DayType.SATURDAY]: 'page:demandForecastDaily.header.dayType.saturday',
  [DayType.HOLIDAY]: 'page:demandForecastDaily.header.dayType.holiday',
  [DayType.SINGULAR]: 'page:demandForecastDaily.header.dayType.singular',
}

export const SectionHeader = ({
  caseId,
  workflowId,
  sectionSegment,
  showTargetDate,
  targetDateLabel,
  dayOfWeekLabel,
  areaLabel,
  demandForecastStatus,
  confirmedAt,
  confirmedBy,
  dayType,
  singularDayName,
  singularDayCategory,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()
  // demandForecastStatus は GetWorkflowCase でのみ populate される
  // (未取得/UNSPECIFIED はバッジ非表示、issue #6624: completedAt からの補完
  // フォールバックは backend が常に populate するようになったため不要)。
  const displayStatus =
    demandForecastStatus !== undefined &&
    demandForecastStatus !== WorkflowCaseStatus.UNSPECIFIED
      ? demandForecastStatus
      : undefined
  const dayTypeI18nKey =
    dayType !== undefined ? dayTypeToI18nKey[dayType] : undefined

  return (
    <Flex
      alignItems="center"
      flexWrap="wrap"
      rowGap={1}
      columnGap={5}
      minHeight="52px"
      marginBottom={4}
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
      paddingX={3}
    >
      {caseId !== undefined && (
        <Flex alignItems="baseline" gap={1}>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('page:demandForecastDaily.header.workflowId')}
          </Text>
          {/* ワークフローID を WorkflowCaseDetail へのリンクにする (design
              L1722-1729 `.ecl-text-link`、item B)。DemandForecastList
              ForecastTable.tsx の workflowId リンクと同一パターン (DS Link +
              RouterLink asChild) を踏襲する。色・下線は DS 既定
              (Text.accentPrimary + hover 下線) が design `.ecl-text-link` と
              同値のため指定しない。太さ・サイズは周囲の値表示 (md/700) と
              揃えるため textStyle="subSectionTitle" を明示する。表示テキストは
              human-readable な workflowId (例: D0-20260729-27D5)、リンク先は
              caseId (UUID) を使う
              (ADR workflow-human-readable-id-separate-from-pk)。 */}
          <Link textStyle="subSectionTitle" asChild>
            <RouterLink
              to={routePaths.workflowCaseDetail.path.replace(':caseId', caseId)}
            >
              {workflowId !== undefined && workflowId !== ''
                ? workflowId
                : caseId}
            </RouterLink>
          </Link>
        </Flex>
      )}
      {showTargetDate && (
        <Flex alignItems="baseline" gap={1}>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('page:demandForecastDaily.header.targetDate')}
          </Text>
          <Text textStyle="subSectionTitle" color="Text.highEmphasis">
            {targetDateLabel}
          </Text>
          {/* 曜日 (design L1771-1778: 「YYYY/MM/DD (曜)」) */}
          {dayOfWeekLabel !== undefined && (
            <Text textStyle="subSectionTitle" color="Text.highEmphasis">
              ({dayOfWeekLabel})
            </Text>
          )}
          {/* 業務エリア名 (design L1764-1778: caseId 経由は Toolbar の業務エリア
              select を非表示にし、代わりに対象日ラベルへ埋め込む) */}
          {areaLabel !== undefined && (
            <Text textStyle="subSectionTitle" color="Text.highEmphasis">
              {areaLabel}
            </Text>
          )}
          {/* 特異日の日付マーカー (AC-06/AC-07/AC-08, spec AC-036a): マーカーは
              対象日ヘッダーの日付表示に隣接配置し、dayType === SINGULAR かつ
              名称が解決済みのときのみ描画する (呼び出し元条件、design doc
              demand-singular-day-display.md §4 主要意思決定「マーカー配置」)。 */}
          {dayType === DayType.SINGULAR &&
            singularDayName !== undefined &&
            singularDayName !== '' && (
              <SingularDayMarker
                category={
                  singularDayCategory ?? SingularDayCategory.UNSPECIFIED
                }
                name={singularDayName}
              />
            )}
        </Flex>
      )}
      {/* 対象日 4 区分バッジ (AC-26, spec AC-036: 平日/土曜/日祝/特異日、AC-05:
          design 突合の色分け)。UNSPECIFIED / 未指定は非表示 (対象日未解決)。
          既存の対象日ラベル (yyyy/mm/dd (曜)) は変更しない別ブロックとして
          追加する (AC-31 回帰防止)。 特異日名は出さない (AC-07)。 */}
      {dayType !== undefined && dayTypeI18nKey !== undefined && (
        <Flex alignItems="baseline" gap={1}>
          <Badge status={dayTypeBadgeStatus(dayType)}>
            {t(dayTypeI18nKey)}
          </Badge>
        </Flex>
      )}
      {sectionSegment !== undefined && (
        <Flex alignItems="baseline" gap={1}>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('page:demandForecastDaily.header.section')}
          </Text>
          <Text textStyle="subSectionTitle" color="Text.highEmphasis">
            {t(sectionSegmentToI18nKey[sectionSegment])}
          </Text>
        </Flex>
      )}
      {/* ケースステータス badge (design L507 + L1704-1712): 未着手/進行中/完了/削除。
          データにある状態のみ表示する (= displayStatus が undefined の時は非表示) */}
      {displayStatus !== undefined && (
        <Flex alignItems="baseline" gap={1}>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('page:demandForecastDaily.header.status')}
          </Text>
          <WorkflowCaseStatusBadge status={displayStatus} />
        </Flex>
      )}
      {/* 確定日時 / 確定者 (design L772-773, AC-DFD-050/051): 需要想定が
          確定済みのときのみ表示する。ワークフロー全体の完了日時・完了者は
          この画面に出さない (AC-DFD-053)。 */}
      {confirmedAt !== undefined && (
        <Flex alignItems="baseline" gap={1}>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('page:demandForecastDaily.header.confirmedAt')}
          </Text>
          <Text
            textStyle="subSectionTitle"
            color="Text.highEmphasis"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatDateTimeLocaleJa(timestampDate(confirmedAt))}
          </Text>
        </Flex>
      )}
      {confirmedAt !== undefined && confirmedBy !== undefined && (
        <Flex alignItems="baseline" gap={1}>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('page:demandForecastDaily.header.confirmedBy')}
          </Text>
          <Text textStyle="subSectionTitle" color="Text.highEmphasis">
            {/* confirmedBy は eauth member ID (auth0 subject)。表示名へ解決し、
                解決不可時は生 ID を出さず — を表示する (#6538)。 */}
            {resolveDisplayName(confirmedBy) ?? '—'}
          </Text>
        </Flex>
      )}
    </Flex>
  )
}
